import * as THREE from 'three';
import {
  HighwayNetwork,
  HighwaySegmentConfig,
  HighwayHeightResult,
  HIGHWAY_TIERS,
  TierConnection,
} from './Highway';
import { HighwaySegment } from './HighwaySegment';

export class HighwayManager {
  private scene: THREE.Scene;
  private segments: HighwaySegment[] = [];
  private spatialGrid: Map<string, HighwaySegment[]> = new Map();
  private connections: TierConnection[] = [];

  private readonly CELL_SIZE = 100;
  private readonly MAP_HALF_SIZE = 900; // Half of 1800x1800 map

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public generate(): void {
    // Generate the default highway network
    const network = this.createDefaultNetwork();

    // Create segments
    for (const segConfig of network.segments) {
      const segment = new HighwaySegment(this.scene, segConfig);
      this.segments.push(segment);
      this.addToSpatialGrid(segment);
    }

    this.connections = network.connections;
  }

  private createDefaultNetwork(): HighwayNetwork {
    const segments: HighwaySegmentConfig[] = [];
    const connections: TierConnection[] = [];

    // === TIER 1: Grid of highways at Y=8 (extended for 1800x1800 map) ===
    const t1Positions = [-300, -100, 100, 300];
    const t1Extent = 650; // Extended from 450 for larger map

    // North-South highways
    for (let i = 0; i < t1Positions.length; i++) {
      const x = t1Positions[i];
      segments.push({
        id: `t1-ns-${i}`,
        startPos: new THREE.Vector3(x, 8, -t1Extent),
        endPos: new THREE.Vector3(x, 8, t1Extent),
        tier: 1,
        width: 12,
        type: 'straight',
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });
    }

    // East-West highways
    for (let i = 0; i < t1Positions.length; i++) {
      const z = t1Positions[i];
      segments.push({
        id: `t1-ew-${i}`,
        startPos: new THREE.Vector3(-t1Extent, 8, z),
        endPos: new THREE.Vector3(t1Extent, 8, z),
        tier: 1,
        width: 12,
        type: 'straight',
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });
    }

    // Ground-to-Tier1 ramps at corners (extended positions for larger map)
    const rampLength = 60;
    const rampCornerDist = 550; // Extended from 400 for larger map
    const rampConfigs = [
      { x: -rampCornerDist, z: -rampCornerDist, dirX: 1, dirZ: 1 },
      { x: rampCornerDist, z: -rampCornerDist, dirX: -1, dirZ: 1 },
      { x: -rampCornerDist, z: rampCornerDist, dirX: 1, dirZ: -1 },
      { x: rampCornerDist, z: rampCornerDist, dirX: -1, dirZ: -1 },
    ];

    for (let i = 0; i < rampConfigs.length; i++) {
      const r = rampConfigs[i];
      segments.push({
        id: `ramp-ground-t1-${i}`,
        startPos: new THREE.Vector3(r.x, 0, r.z),
        endPos: new THREE.Vector3(r.x + r.dirX * rampLength, 8, r.z + r.dirZ * rampLength),
        tier: 1,
        width: 10,
        type: 'ramp',
        startHeight: 0,
        endHeight: 8,
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });

      connections.push({
        id: `conn-ground-t1-${i}`,
        type: 'ramp',
        fromTier: 0,
        toTier: 1,
        entry: new THREE.Vector3(r.x, 0, r.z),
        exit: new THREE.Vector3(r.x + r.dirX * rampLength, 8, r.z + r.dirZ * rampLength),
      });
    }

    // === TIER 2: X-pattern diagonals at Y=18 (extended for larger map) ===
    const t2Extent = 550; // Extended from 380 for larger map

    // NW to SE diagonal
    segments.push({
      id: 't2-nw-se',
      startPos: new THREE.Vector3(-t2Extent, 18, t2Extent),
      endPos: new THREE.Vector3(t2Extent, 18, -t2Extent),
      tier: 2,
      width: 14,
      type: 'straight',
      hasGuardrails: true,
      hasNeonStripes: true,
      requiresJump: false,
    });

    // NE to SW diagonal
    segments.push({
      id: 't2-ne-sw',
      startPos: new THREE.Vector3(t2Extent, 18, t2Extent),
      endPos: new THREE.Vector3(-t2Extent, 18, -t2Extent),
      tier: 2,
      width: 14,
      type: 'straight',
      hasGuardrails: true,
      hasNeonStripes: true,
      requiresJump: false,
    });

    // T1-to-T2 ramps at the cross points
    const t2RampConfigs = [
      { x: -200, z: 200, dirX: 1, dirZ: -1 }, // NW area
      { x: 200, z: 200, dirX: -1, dirZ: -1 }, // NE area
      { x: -200, z: -200, dirX: 1, dirZ: 1 }, // SW area
      { x: 200, z: -200, dirX: -1, dirZ: 1 }, // SE area
    ];

    for (let i = 0; i < t2RampConfigs.length; i++) {
      const r = t2RampConfigs[i];
      segments.push({
        id: `ramp-t1-t2-${i}`,
        startPos: new THREE.Vector3(r.x, 8, r.z),
        endPos: new THREE.Vector3(r.x + r.dirX * 50, 18, r.z + r.dirZ * 50),
        tier: 2,
        width: 10,
        type: 'ramp',
        startHeight: 8,
        endHeight: 18,
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });

      connections.push({
        id: `conn-t1-t2-${i}`,
        type: 'ramp',
        fromTier: 1,
        toTier: 2,
        entry: new THREE.Vector3(r.x, 8, r.z),
        exit: new THREE.Vector3(r.x + r.dirX * 50, 18, r.z + r.dirZ * 50),
      });
    }

    // === TIER 3: Octagonal beltway at Y=30 (extended for larger map) ===
    const t3Radius = 580; // Extended from 420 for larger map
    const t3Corners = 8;
    const t3Nodes: THREE.Vector3[] = [];

    for (let i = 0; i < t3Corners; i++) {
      const angle = (i / t3Corners) * Math.PI * 2;
      t3Nodes.push(new THREE.Vector3(
        Math.cos(angle) * t3Radius,
        30,
        Math.sin(angle) * t3Radius
      ));
    }

    // Connect T3 nodes in a ring
    for (let i = 0; i < t3Corners; i++) {
      const nextI = (i + 1) % t3Corners;
      segments.push({
        id: `t3-ring-${i}`,
        startPos: t3Nodes[i],
        endPos: t3Nodes[nextI],
        tier: 3,
        width: 16,
        type: 'straight',
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });
    }

    // T2-to-T3 ramps at cardinal directions (adjusted for larger beltway)
    const t3RampAngles = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];
    const t3InnerRadius = 450; // Extended from 320 to reach new beltway
    for (let i = 0; i < t3RampAngles.length; i++) {
      const angle = t3RampAngles[i];
      const innerRadius = t3InnerRadius;
      const startX = Math.cos(angle) * innerRadius;
      const startZ = Math.sin(angle) * innerRadius;
      const endX = Math.cos(angle) * t3Radius;
      const endZ = Math.sin(angle) * t3Radius;

      segments.push({
        id: `ramp-t2-t3-${i}`,
        startPos: new THREE.Vector3(startX, 18, startZ),
        endPos: new THREE.Vector3(endX, 30, endZ),
        tier: 3,
        width: 12,
        type: 'ramp',
        startHeight: 18,
        endHeight: 30,
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: false,
      });

      connections.push({
        id: `conn-t2-t3-${i}`,
        type: 'ramp',
        fromTier: 2,
        toTier: 3,
        entry: new THREE.Vector3(startX, 18, startZ),
        exit: new THREE.Vector3(endX, 30, endZ),
      });
    }

    // === TIER 4: Isolated sky loops at Y=45 (jump-only access) ===
    // Northwest sky loop
    const t4NWCenter = new THREE.Vector3(-350, 45, 350);
    const t4Radius = 60;
    const t4Segments = 6;

    for (let i = 0; i < t4Segments; i++) {
      const angle1 = (i / t4Segments) * Math.PI * 2;
      const angle2 = ((i + 1) / t4Segments) * Math.PI * 2;
      segments.push({
        id: `t4-nw-${i}`,
        startPos: new THREE.Vector3(
          t4NWCenter.x + Math.cos(angle1) * t4Radius,
          45,
          t4NWCenter.z + Math.sin(angle1) * t4Radius
        ),
        endPos: new THREE.Vector3(
          t4NWCenter.x + Math.cos(angle2) * t4Radius,
          45,
          t4NWCenter.z + Math.sin(angle2) * t4Radius
        ),
        tier: 4,
        width: 10,
        type: 'straight',
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: true,
      });
    }

    // Southeast sky loop
    const t4SECenter = new THREE.Vector3(350, 45, -350);
    for (let i = 0; i < t4Segments; i++) {
      const angle1 = (i / t4Segments) * Math.PI * 2;
      const angle2 = ((i + 1) / t4Segments) * Math.PI * 2;
      segments.push({
        id: `t4-se-${i}`,
        startPos: new THREE.Vector3(
          t4SECenter.x + Math.cos(angle1) * t4Radius,
          45,
          t4SECenter.z + Math.sin(angle1) * t4Radius
        ),
        endPos: new THREE.Vector3(
          t4SECenter.x + Math.cos(angle2) * t4Radius,
          45,
          t4SECenter.z + Math.sin(angle2) * t4Radius
        ),
        tier: 4,
        width: 10,
        type: 'straight',
        hasGuardrails: true,
        hasNeonStripes: true,
        requiresJump: true,
      });
    }

    // Jump ramps from T3 to T4 (adjusted for larger beltway)
    // NW jump ramp (beltway diagonal position ~410)
    connections.push({
      id: 'jump-t3-t4-nw',
      type: 'jump',
      fromTier: 3,
      toTier: 4,
      entry: new THREE.Vector3(-410, 30, 410),
      exit: t4NWCenter.clone(),
    });

    // SE jump ramp (beltway diagonal position ~410)
    connections.push({
      id: 'jump-t3-t4-se',
      type: 'jump',
      fromTier: 3,
      toTier: 4,
      entry: new THREE.Vector3(410, 30, -410),
      exit: t4SECenter.clone(),
    });

    return {
      tiers: HIGHWAY_TIERS,
      nodes: new Map(),
      segments,
      connections,
    };
  }

  private getCellKey(x: number, z: number): string {
    const cx = Math.floor((x + this.MAP_HALF_SIZE) / this.CELL_SIZE);
    const cz = Math.floor((z + this.MAP_HALF_SIZE) / this.CELL_SIZE);
    return `${cx},${cz}`;
  }

  private addToSpatialGrid(segment: HighwaySegment): void {
    const bounds = segment.getBounds();

    // Get all cells this segment touches
    const minCX = Math.floor((bounds.minX + this.MAP_HALF_SIZE) / this.CELL_SIZE);
    const maxCX = Math.floor((bounds.maxX + this.MAP_HALF_SIZE) / this.CELL_SIZE);
    const minCZ = Math.floor((bounds.minZ + this.MAP_HALF_SIZE) / this.CELL_SIZE);
    const maxCZ = Math.floor((bounds.maxZ + this.MAP_HALF_SIZE) / this.CELL_SIZE);

    for (let cx = minCX; cx <= maxCX; cx++) {
      for (let cz = minCZ; cz <= maxCZ; cz++) {
        const key = `${cx},${cz}`;
        if (!this.spatialGrid.has(key)) {
          this.spatialGrid.set(key, []);
        }
        this.spatialGrid.get(key)!.push(segment);
      }
    }
  }

  public getHeightAtPosition(worldPos: THREE.Vector3): HighwayHeightResult {
    const cellKey = this.getCellKey(worldPos.x, worldPos.z);
    const candidates = this.spatialGrid.get(cellKey) || [];

    let result: HighwayHeightResult = {
      height: 0,
      onHighway: false,
      currentTier: 0,
      segmentId: null,
    };

    let maxHeight = -Infinity;

    for (const segment of candidates) {
      const height = segment.getHeightAtPosition(worldPos);
      if (height !== null && height > maxHeight) {
        maxHeight = height;
        result = {
          height,
          onHighway: true,
          currentTier: segment.config.tier,
          segmentId: segment.config.id,
        };
      }
    }

    return result;
  }

  public getConnections(): TierConnection[] {
    return this.connections;
  }

  public getSegments(): HighwaySegment[] {
    return this.segments;
  }

  public getNearestConnection(pos: THREE.Vector3, targetTier: number): TierConnection | null {
    let nearest: TierConnection | null = null;
    let minDist = Infinity;

    for (const conn of this.connections) {
      if (conn.toTier === targetTier) {
        const dist = pos.distanceTo(conn.entry);
        if (dist < minDist) {
          minDist = dist;
          nearest = conn;
        }
      }
    }

    return nearest;
  }

  public isOnHighway(pos: THREE.Vector3): boolean {
    return this.getHeightAtPosition(pos).onHighway;
  }

  public destroy(): void {
    for (const segment of this.segments) {
      segment.destroy(this.scene);
    }
    this.segments = [];
    this.spatialGrid.clear();
    this.connections = [];
  }
}
